import { IconCircleX } from "@/assets/icons/central/IconCircleX";
import type { BaseIconProps } from "@/assets/icons/utils";
import type { PROMPT_TEMPLATE_SIZES } from "@/components/ai/ai-constants";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import type { PropsWithTestId } from "@/components/podkit/lib/testid";
import type { WithTrackingId } from "@/types/tracking";
import { useCallback, useId } from "react";

export type PromptTemplateSize = (typeof PROMPT_TEMPLATE_SIZES)[number];
type SelectionBehavior = "select" | "dismiss";

type Props = {
    Icon: React.ComponentType<BaseIconProps>;
    iconColor: string;
    iconBackgroundColor: string;
    title: string;
    /**
     * Only shown when `size === "lg"`.
     */
    description: string;
    selected: boolean;
    size?: PromptTemplateSize;
    /**
     * If "select", the template will act as a checkbox. If "dismiss", the template will be a card with a close button.
     */
    selectionBehavior?: SelectionBehavior;
    onSelectChange: (selected: boolean) => void;
} & WithTrackingId & PropsWithClassName &
    PropsWithTestId;

export function PromptTemplate({
    className,
    "data-testid": testId,
    "data-tracking-id": trackingId,
    Icon,
    iconColor,
    iconBackgroundColor,
    title,
    description,
    selected,
    size = "lg",
    onSelectChange,
    selectionBehavior = "select",
}: Props) {
    const inputId = useId();

    const onChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            onSelectChange(event.target.checked);
        },
        [onSelectChange],
    );

    const ContentComp = selectionBehavior === "select" ? "label" : "div";
    const contentProps = selectionBehavior === "select" ? { htmlFor: inputId } : {};

    return (
        <div
            className={cn(
                "w-56",
                {
                    "w-fit": size === "sm", // small prompt templates are as narrow as possible
                },
                className,
            )}
        >
            {selectionBehavior === "select" && (
                <input id={inputId} checked={selected} onChange={onChange} type="checkbox" className="peer sr-only" />
            )}
            {/* to prevent layout shift caused by border width changes (border-2 when selected, border-1 when not)
                an additional padding of 1px is applied when unselected to compensate for the thinner border */}
            <ContentComp
                data-testid={testId}
                data-tracking-id={trackingId}
                className={cn(
                    "relative box-border flex h-full flex-col items-start justify-start rounded-xl border border-border-subtle bg-surface-secondary shadow-sm ring-offset-1 peer-checked:border-2 peer-checked:border-content-brand peer-focus-visible:ring-2 peer-focus-visible:ring-content-primary",
                    {
                        "flex-row items-center gap-1.5 rounded-lg p-[calc(0.5rem+1px)] pr-[calc(0.75rem+1px)] peer-checked:p-2 peer-checked:pr-3":
                            size === "sm",
                        "flex-row items-center gap-1.5 p-[calc(0.75rem+1px)] peer-checked:p-3": size === "base",
                        "p-[calc(1.25rem+1px)] peer-checked:p-5": size === "lg",
                        "cursor-pointer transition-[background-color,box-shadow,transform] duration-100 hover:bg-surface-button-secondary hover:shadow-md active:scale-[99%] active:shadow-inner":
                            selectionBehavior === "select",
                    },
                )}
                {...contentProps}
            >
                <PromptIcon Icon={Icon} color={iconColor} backgroundColor={iconBackgroundColor} />
                <span
                    className={cn("text-base text-content-primary", {
                        "mt-2 font-medium": size === "lg",
                        "mr-3": size === "sm" && selectionBehavior === "dismiss",
                    })}
                >
                    {title}
                </span>
                <span
                    className={cn("text-base text-content-muted", {
                        "sr-only": size === "sm" || size === "base",
                    })}
                >
                    {description}
                </span>
                {selectionBehavior === "dismiss" && (
                    <button
                        onClick={() => onSelectChange(false)}
                        aria-label={`Delete "${title}" task`}
                        className="absolute right-2 top-2 flex size-3.5 items-center justify-center rounded-full p-0"
                        data-tracking-id={trackingId || "default-tracking-id"}
                    >
                        <IconCircleX className="size-4 text-content-muted" />
                    </button>
                )}
            </ContentComp>
        </div>
    );
}

function PromptIcon({
    Icon,
    color,
    backgroundColor,
}: {
    Icon: React.ComponentType<BaseIconProps>;
    color: string;
    backgroundColor: string;
}) {
    return (
        <span
            className="flex size-6 flex-shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor, color }}
        >
            <Icon size="base" className="size-4" />
        </span>
    );
}
